import React, { useState, useEffect } from 'react';
import {
  FaTachometerAlt,
  FaUsers,
  FaTicketAlt,
  FaChartBar,
  FaCog,
  FaBell,
  FaSearch,
  FaFilter,
  FaSort,
  FaEye,
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaClock,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFileAlt,
  FaDownload,
  FaUpload,
  FaComments,
  FaHistory,
  FaUserCheck,
  FaUserTimes,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPause,
  FaPlay,
  FaArchive,
  FaPrint,
  FaShare,
  FaTag,
  FaTruckMoving,
  FaBan,
  FaCalendar,
  FaDollarSign,
  FaCreditCard,
  FaShieldAlt,
  FaLock,
  FaSignOutAlt,
  FaBars,
  FaUserShield,
  FaChevronLeft,
  FaChevronRight,
  FaFileInvoice,
  FaCode,
  FaWifi,
  FaServer,
  FaBuilding,
  FaClipboardList,
  FaStethoscope
} from 'react-icons/fa';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import CustomerManagement from './components/CustomerManagement';
import CustomerMap from './components/CustomerMap';
import BillingManagement from './components/BillingManagement';
import MoveRequestsManagement from './components/MoveRequestsManagement';
import CancelRequestsManagement from './components/CancelRequestsManagement';
import UnfinishedSignups from './components/UnfinishedSignups';
import PlanManagement from './components/PlanManagement';
import TicketSystem from './components/TicketSystem';
import Analytics from './components/Analytics';
import SeoDashboard from './components/SeoDashboard';
import Settings from './components/Settings';
import ReminderSettings from './components/ReminderSettings';
import TemplateManager from './components/TemplateManager';
import AdminUserManagement from './components/AdminUserManagement';
import RolePermissionsManagement from './components/RolePermissionsManagement';
import ITServicesQuoteManagement from './components/ITServicesQuoteManagement';
import ITServicesInvoiceManagement from './components/ITServicesInvoiceManagement';
import ContractManagement from './components/ContractManagement';
import VendorInvoiceManagement from './components/VendorInvoiceManagement';
import ITServicesCustomerManagement from './components/ITServicesCustomerManagement';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionGate from './components/PermissionGate';
import LiveChat from './components/LiveChat';
import NbnAvailabilityCheck from './components/NbnAvailabilityCheck';
import ProvisionTool from './components/ProvisionTool';
import NewBusinessCustomer from './components/NewBusinessCustomer';
import ServiceOrders from './components/ServiceOrders';
import ServiceDiagnostics from './components/ServiceDiagnostics';
import { API_ENDPOINTS } from '../config/api';
import { LOGO_URL } from '../constants/branding';

// Inner component that uses auth context
const AdminPortalContent = () => {
  const { user, isAuthenticated, loading, logout } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Enhanced stats with analytics data
  const [stats, setStats] = useState({
    totalCustomers: 0,
    pendingVerifications: 0,
    activeTickets: 0,
    monthlyRevenue: 0,
    ddSuccess: 0,
    ddRejected: 0,
    ddSuccessRate: 0,
    totalRevenue: 0,
    activeServices: 0,
    lastMonthRevenue: 0,
    planDistribution: {},
    recentActivity: []
  });

  // Deep links: https://admin.peaknet.com.au/?tab=itservices-quotes
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab) setActiveTab(tab);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    // Load dashboard data when authenticated
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      // Real API call to get customer statistics
      const response = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.stats) {
        // Use real stats from API. ddSuccessRate is null when the Lambda has
        // no real invoice data — propagate the null so the dashboard renders
        // "—" instead of inventing a "0%" (or worse, the old fake 85%).
        setStats({
          totalCustomers: data.stats.total || 0,
          pendingVerifications: data.stats.pending || 0,
          activeTickets: data.stats.activeTickets || 0,
          monthlyRevenue: data.stats.monthlyRevenue || 0,
          lastMonthRevenue: data.stats.lastMonthRevenue || 0,
          lastMonthCustomers: data.stats.lastMonthCustomers || 0,
          lastMonthServices: data.stats.lastMonthServices || 0,
          lastMonthDdRate: data.stats.lastMonthDdRate,
          ddSuccess: data.stats.ddSuccess || 0,
          ddRejected: data.stats.ddRejected || 0,
          ddSuccessRate: data.stats.ddSuccessRate, // may be null
          totalRevenue: data.stats.totalRevenue || 0,
          activeServices: data.stats.activeServices || 0,
          planDistribution: data.stats.planDistribution || {},
          recentActivity: data.stats.recentActivity || [],
          dataQuality: data.stats.dataQuality || null,
        });
      } else {
        // No silent mock fallback — surface the failure so admin knows
        // the numbers on the dashboard aren't real.
        console.error('API returned error:', data.error);
        setStats((prev) => ({
          ...prev,
          dataQuality: { ...(prev.dataQuality || {}), apiFailed: true, apiError: data.error || 'Unknown API error' },
        }));
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setStats((prev) => ({
        ...prev,
        dataQuality: { ...(prev.dataQuality || {}), apiFailed: true, apiError: error.message },
      }));
    }
  };

  const handleLoginSuccess = (adminUser) => {
    // Auth context handles this, just load dashboard data
    loadDashboardData();
  };

  const handleLogout = () => {
    logout();
    setActiveTab('dashboard');
    setStats({
      totalCustomers: 0,
      pendingVerifications: 0,
      activeTickets: 0,
      monthlyRevenue: 0,
      ddSuccess: 0,
      ddRejected: 0,
      ddSuccessRate: 0,
      totalRevenue: 0,
      activeServices: 0,
      lastMonthRevenue: 0,
      planDistribution: {},
      recentActivity: []
    });
  };

  // Helper: Check if user has permission
  const hasPermission = (permission) => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permission);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <ProtectedRoute><AdminDashboard stats={stats} onNavigate={setActiveTab} /></ProtectedRoute>;
      case 'customers':
        return <ProtectedRoute requiredPermission="customers:read"><CustomerManagement /></ProtectedRoute>;
      case 'customer-map':
        return <ProtectedRoute requiredPermission="customers:read"><CustomerMap /></ProtectedRoute>;
      case 'billing':
        return <ProtectedRoute requiredPermission="billing:read"><BillingManagement /></ProtectedRoute>;
      case 'plans':
        return <ProtectedRoute requiredPermission="plans:read"><PlanManagement /></ProtectedRoute>;
      case 'tickets':
        return <ProtectedRoute requiredPermission="tickets:read"><TicketSystem /></ProtectedRoute>;
      case 'live-chat':
        return <ProtectedRoute requiredPermission="tickets:read"><LiveChat /></ProtectedRoute>;
      case 'analytics':
        return <ProtectedRoute requiredPermission="customers:read"><Analytics /></ProtectedRoute>;
      case 'seo':
        return <ProtectedRoute requiredPermission="customers:read"><SeoDashboard /></ProtectedRoute>;
      case 'users':
        return <ProtectedRoute requiredPermission="admin_users:read"><AdminUserManagement /></ProtectedRoute>;
      case 'role-permissions':
        return <ProtectedRoute requiredPermission="system:settings"><RolePermissionsManagement /></ProtectedRoute>;
      case 'itservices-quotes':
        return <ProtectedRoute requiredPermission="customers:read"><ITServicesQuoteManagement onNavigate={setActiveTab} /></ProtectedRoute>;
      case 'itservices-invoices':
        return <ProtectedRoute requiredPermission="customers:read"><ITServicesInvoiceManagement onNavigate={setActiveTab} /></ProtectedRoute>;
      case 'contracts':
        return <ProtectedRoute requiredPermission="customers:read"><ContractManagement /></ProtectedRoute>;
      case 'itservices-customers':
        return <ProtectedRoute requiredPermission="customers:read"><ITServicesCustomerManagement /></ProtectedRoute>;
      case 'vendor-invoices':
        return <ProtectedRoute requiredPermission="customers:read"><VendorInvoiceManagement /></ProtectedRoute>;
      case 'reminders':
        return <ProtectedRoute requiredPermission="system:settings"><ReminderSettings /></ProtectedRoute>;
      case 'templates':
        return <ProtectedRoute requiredPermission="system:settings"><TemplateManager /></ProtectedRoute>;
      case 'nbn-check':
        return <ProtectedRoute requiredPermission="system:settings"><NbnAvailabilityCheck /></ProtectedRoute>;
      case 'provision':
        return <ProtectedRoute requiredPermission="system:settings"><ProvisionTool /></ProtectedRoute>;
      case 'new-business-customer':
        return <ProtectedRoute requiredPermission="customers:write"><NewBusinessCustomer /></ProtectedRoute>;
      case 'service-orders':
        return <ProtectedRoute requiredPermission="system:settings"><ServiceOrders /></ProtectedRoute>;
      case 'service-diagnostics':
        return <ProtectedRoute requiredPermission="system:settings"><ServiceDiagnostics /></ProtectedRoute>;
      case 'move-requests':
        return <ProtectedRoute requiredPermission="customers:read"><MoveRequestsManagement /></ProtectedRoute>;
      case 'unfinished-signups':
        return <ProtectedRoute requiredPermission="customers:read"><UnfinishedSignups /></ProtectedRoute>;
      case 'cancel-requests':
        return <ProtectedRoute requiredPermission="customers:read"><CancelRequestsManagement /></ProtectedRoute>;
      case 'settings':
        return <ProtectedRoute><Settings /></ProtectedRoute>;
      default:
        return <ProtectedRoute><AdminDashboard stats={stats} onNavigate={setActiveTab} /></ProtectedRoute>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#282828] to-[#1d2021] flex items-center justify-center">
        <div className="text-center text-[#ebdbb2]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#fabd2f] mx-auto mb-4"></div>
          <p className="text-lg">Loading Admin Portal...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Show main admin interface if authenticated
  return (
    <div className="min-h-screen bg-[#1d2021] text-[#ebdbb2]">
      {/* Header */}
      <header className="bg-[#282828] shadow-sm border-b border-[#3c3836]">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[#bdae93] hover:text-[#ebdbb2] lg:hidden"
            >
              <FaBars size={20} />
            </button>
            <div className="flex items-center space-x-3">
              <div className="inline-flex items-center justify-center rounded-xl px-3 py-1.5 bg-gradient-to-r from-[#FFD700] to-[#FFB700] shadow-lg">
                <img src={LOGO_URL} alt="Peaknet" className="h-8 w-auto block select-none" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#ebdbb2]">Peaknet Admin</h1>
                <p className="text-sm text-[#bdae93]">Customer Management & Support</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative hidden md:block">
              <input
                type="text"
                placeholder="Search customers, tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
              />
              <FaSearch className="absolute left-3 top-3 text-[#7c6f64]" />
            </div>

            {/* Notifications */}
            <button className="relative p-2 text-[#bdae93] hover:text-[#ebdbb2] hover:bg-[#3c3836] rounded-lg">
              <FaBell size={20} />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#fb4934] text-[#1d2021] text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {notifications.length}
                </span>
              )}
            </button>

            {/* User Menu */}
            <div className="flex items-center space-x-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-[#ebdbb2]">{user?.name}</p>
                <p className="text-xs text-[#bdae93]">{user?.email}</p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                {user?.avatar || 'A'}
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-[#bdae93] hover:text-[#fb4934] hover:bg-[#3c3836] rounded-lg"
                title="Logout"
              >
                <FaSignOutAlt size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed lg:static lg:translate-x-0 z-30 ${sidebarCollapsed ? 'w-16' : 'w-64'} h-[calc(100vh-73px)] bg-[#282828] shadow-lg lg:shadow-none transition-all duration-300 ease-in-out overflow-x-hidden`}>
          <div className="h-full flex flex-col overflow-x-hidden">
            <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden sidebar-scroll">
              <div className="py-6">
                <nav className="space-y-2 px-2 overflow-x-hidden">
                  <NavItem
                    icon={<FaTachometerAlt />}
                    label="Dashboard"
                    active={activeTab === 'dashboard'}
                    onClick={() => setActiveTab('dashboard')}
                    badge={notifications.length}
                    collapsed={sidebarCollapsed}
                  />
                  {(hasPermission('customers:read')) && (
                    <NavItem
                      icon={<FaUsers />}
                      label="Customers"
                      active={activeTab === 'customers'}
                      onClick={() => setActiveTab('customers')}
                      badge={stats.pendingVerifications}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:read')) && (
                    <NavItem
                      icon={<FaMapMarkerAlt />}
                      label="Customer Map"
                      active={activeTab === 'customer-map'}
                      onClick={() => setActiveTab('customer-map')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('billing:read')) && (
                    <NavItem
                      icon={<FaDollarSign />}
                      label="Billing Management"
                      active={activeTab === 'billing'}
                      onClick={() => setActiveTab('billing')}
                      badge={stats.failedPayments}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('plans:read')) && (
                    <NavItem
                      icon={<FaTag />}
                      label="Plan Management"
                      active={activeTab === 'plans'}
                      onClick={() => setActiveTab('plans')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('system:settings')) && (
                    <NavItem
                      icon={<FaWifi />}
                      label="NBN Availability Check"
                      active={activeTab === 'nbn-check'}
                      onClick={() => setActiveTab('nbn-check')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:write')) && (
                    <NavItem
                      icon={<FaBuilding />}
                      label="New Business Customer"
                      active={activeTab === 'new-business-customer'}
                      onClick={() => setActiveTab('new-business-customer')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('system:settings')) && (
                    <NavItem
                      icon={<FaServer />}
                      label="Provision Service"
                      active={activeTab === 'provision'}
                      onClick={() => setActiveTab('provision')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('system:settings')) && (
                    <NavItem
                      icon={<FaClipboardList />}
                      label="Service Orders"
                      active={activeTab === 'service-orders'}
                      onClick={() => setActiveTab('service-orders')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('system:settings')) && (
                    <NavItem
                      icon={<FaStethoscope />}
                      label="Diagnostics"
                      active={activeTab === 'service-diagnostics'}
                      onClick={() => setActiveTab('service-diagnostics')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('tickets:read')) && (
                    <NavItem
                      icon={<FaComments />}
                      label="Live Chat"
                      active={activeTab === 'live-chat'}
                      onClick={() => setActiveTab('live-chat')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('tickets:read')) && (
                    <NavItem
                      icon={<FaTicketAlt />}
                      label="Tickets"
                      active={activeTab === 'tickets'}
                      onClick={() => setActiveTab('tickets')}
                      badge={stats.activeTickets}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:read')) && (
                    <NavItem
                      icon={<FaChartBar />}
                      label="Analytics"
                      active={activeTab === 'analytics'}
                      onClick={() => setActiveTab('analytics')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:read')) && (
                    <NavItem
                      icon={<FaChartBar />}
                      label="SEO Health"
                      active={activeTab === 'seo'}
                      onClick={() => setActiveTab('seo')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:read')) && (
                    <NavItem
                      icon={<FaTruckMoving />}
                      label="Move Requests"
                      active={activeTab === 'move-requests'}
                      onClick={() => setActiveTab('move-requests')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:read')) && (
                    <NavItem
                      icon={<FaEnvelope />}
                      label="Unfinished Signups"
                      active={activeTab === 'unfinished-signups'}
                      onClick={() => setActiveTab('unfinished-signups')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:read')) && (
                    <NavItem
                      icon={<FaBan />}
                      label="Cancel Requests"
                      active={activeTab === 'cancel-requests'}
                      onClick={() => setActiveTab('cancel-requests')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('customers:read')) && (
                    <>
                      <NavItem
                        icon={<FaFileInvoice />}
                        label="IT Services Quotes"
                        active={activeTab === 'itservices-quotes'}
                        onClick={() => setActiveTab('itservices-quotes')}
                        collapsed={sidebarCollapsed}
                      />
                      <NavItem
                        icon={<FaFileInvoice />}
                        label="IT Services Invoices"
                        active={activeTab === 'itservices-invoices'}
                        onClick={() => setActiveTab('itservices-invoices')}
                        collapsed={sidebarCollapsed}
                      />
                      <NavItem
                        icon={<FaUsers />}
                        label="IT Services Customers"
                        active={activeTab === 'itservices-customers'}
                        onClick={() => setActiveTab('itservices-customers')}
                        collapsed={sidebarCollapsed}
                      />
                    </>
                  )}
                  {(hasPermission('customers:read')) && (
                    <>
                      <NavItem
                        icon={<FaFileAlt />}
                        label="Contract Management"
                        active={activeTab === 'contracts'}
                        onClick={() => setActiveTab('contracts')}
                        collapsed={sidebarCollapsed}
                      />
                      <NavItem
                        icon={<FaFileInvoice />}
                        label="Vendor Invoices"
                        active={activeTab === 'vendor-invoices'}
                        onClick={() => setActiveTab('vendor-invoices')}
                        collapsed={sidebarCollapsed}
                      />
                    </>
                  )}
                  {(hasPermission('admin_users:read')) && (
                    <NavItem
                      icon={<FaUserShield />}
                      label="Admin Users"
                      active={activeTab === 'users'}
                      onClick={() => setActiveTab('users')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('system:settings')) && (
                    <NavItem
                      icon={<FaShieldAlt />}
                      label="Role Permissions"
                      active={activeTab === 'role-permissions'}
                      onClick={() => setActiveTab('role-permissions')}
                      collapsed={sidebarCollapsed}
                    />
                  )}
                  {(hasPermission('system:settings')) && (
                    <>
                      <NavItem
                        icon={<FaClock />}
                        label="Automated Reminders"
                        active={activeTab === 'reminders'}
                        onClick={() => setActiveTab('reminders')}
                        collapsed={sidebarCollapsed}
                      />
                      <NavItem
                        icon={<FaCode />}
                        label="Template Manager"
                        active={activeTab === 'templates'}
                        onClick={() => setActiveTab('templates')}
                        collapsed={sidebarCollapsed}
                      />
                    </>
                  )}
                  <NavItem
                    icon={<FaCog />}
                    label="Settings"
                    active={activeTab === 'settings'}
                    onClick={() => setActiveTab('settings')}
                    collapsed={sidebarCollapsed}
                  />
                </nav>
              </div>

            </div>

            {/* Collapse Toggle Button - Bottom */}
            <div className="p-3 border-t border-[#3c3836] flex items-center justify-center">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-[#fabd2f] text-[#1d2021] hover:bg-[#fe8019] shadow-md transition-colors"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <FaChevronRight size={16} /> : <FaChevronLeft size={16} />}
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-0'} overflow-y-auto`}>
          <div className="p-6">
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// Navigation Item Component
const NavItem = ({ icon, label, active, onClick, badge, collapsed }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} ${collapsed ? 'px-2' : 'px-4'} py-3 rounded-lg ${collapsed ? '' : 'text-left'} transition-colors relative group min-w-0 overflow-hidden ${active
      ? 'bg-gradient-to-r from-[#fabd2f] to-[#fe8019] text-[#1d2021] shadow-lg'
      : 'text-[#ebdbb2] hover:bg-[#3c3836] hover:text-[#fbf1c7]'
      }`}
    title={collapsed ? label : ''}
  >
    <div className={`flex items-center ${collapsed ? '' : 'space-x-3'} min-w-0 flex-1`}>
      <span className="text-lg flex-shrink-0">{icon}</span>
      {!collapsed && <span className="font-medium truncate">{label}</span>}
    </div>
    {!collapsed && (badge || 0) > 0 && (
      <span className={`px-2 py-1 text-xs rounded-full ${active ? 'bg-[#1d2021] text-[#fabd2f]' : 'bg-[#cc241d] text-[#ebdbb2]'
        }`}>
        {badge}
      </span>
    )}
    {/* Tooltip for collapsed state */}
    {collapsed && (
      <div className="absolute left-full ml-2 px-3 py-2 bg-[#1d2021] text-[#ebdbb2] text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 border border-[#3c3836]">
        {label}
      </div>
    )}
  </button>
);

// Main component wrapped with Auth Provider
const AdminPortalWrapper = () => {
  return (
    <AdminAuthProvider>
      <AdminPortalContent />
    </AdminAuthProvider>
  );
};

export default AdminPortalWrapper;